import React, { useEffect, useMemo, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState } from "@/hq/HQPrimitives";
import AddSubscriptionModal from "@/hq/clients/AddSubscriptionModal";
import SubscriptionEmailPreviewModal from "@/hq/clients/SubscriptionEmailPreviewModal";
import { toast } from "sonner";
import { Plus, Mail, Copy, XCircle, Loader2, CheckCircle2, Circle } from "lucide-react";

const CUR_SYMBOL = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const money = (minor, cur) => `${CUR_SYMBOL[cur] || cur + " "}${(Number(minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (v) => { if (!v) return "—"; try { return new Date(v).toLocaleDateString(); } catch { return "—"; } };

const STATUS_TONE = {
  pending_setup:    { fg: "#8A6D00", bg: "#FFF9E6", bd: "#F5E4B0", label: "Pending setup" },
  awaiting_payment: { fg: "#8A4A00", bg: "#FFF7EE", bd: "#FCE1C7", label: "Awaiting payment" },
  draft:            { fg: "#8A6D00", bg: "#FFF9E6", bd: "#F5E4B0", label: "Draft" },
  active:           { fg: "#0FA958", bg: "#EBFBF2", bd: "#B7EBCF", label: "Active" },
  paused:           { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA", label: "Paused" },
  cancelled:        { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA", label: "Cancelled" },
  failed:           { fg: "#C1350B", bg: "#FFECEC", bd: "#FCE1C7", label: "Failed" },
  pending:          { fg: "#8A6D00", bg: "#FFF9E6", bd: "#F5E4B0", label: "Pending" },
};

const SERVICE_LABELS = {
  landing_page:         "Landing Page",
  premium_website:      "Full / Custom Website",
  crm_business_system:  "Custom CRM & Business Systems",
  orange_care:          "OrangeCare",
  orange_optimise_pro:  "Orange Optimise Pro",
  orange_optimise:      "Orange Optimise",
};

function StatusPill({ status }) {
  const t = STATUS_TONE[status] || { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA", label: status || "—" };
  return (
    <span
      data-testid={`sub-status-${status}`}
      className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.14em] uppercase"
      style={{ color: t.fg, background: t.bg, border: `1px solid ${t.bd}` }}
    >
      {t.label}
    </span>
  );
}

/**
 * Phase 3I.4O — Bundle-aware Client Profile Subscriptions tab.
 *
 * Renders every subscription as a canonical card. When the subscription
 * carries `bundle_id`, both underlying services + entitlements + agreement
 * statuses are displayed. Every price is read from the immutable
 * `bundle_lines`/`quote_snapshot`/`amount_minor` stored on the row —
 * NEVER recomputed here.
 */
export default function ClientSubscriptionsTab({ businessId, primaryContact, primaryEmail, businessName, onServiceChange }) {
  const [items, setItems] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [previewFor, setPreviewFor] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    try {
      const r = await hqApi.get(`/control/clients/${businessId}/subscriptions`);
      setItems(r.data.items || []);
    } catch {
      toast.error("Could not load subscriptions");
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [businessId]);

  const onDraftCreated = (draft) => {
    setAddOpen(false);
    reload();
    // Bundle draft response uses "subscription" object; legacy uses subscription_id.
    const subId = draft.subscription_id || draft.subscription?.id;
    if (!draft.bundle_id && subId) {
      setPreviewFor({
        subscription_id: subId,
        to_email: draft.to_email, to_name: draft.to_name,
      });
    } else {
      toast.success("Combined subscription created");
    }
  };

  const cancelSub = async (subId) => {
    if (!window.confirm("Cancel this subscription?")) return;
    setBusyId(subId);
    try {
      await hqApi.post(`/control/clients/${businessId}/subscriptions/${subId}/cancel`, { reason: "hq_manual_cancel" });
      toast.success("Subscription cancelled");
      reload();
      onServiceChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="p-6" data-testid="hq-client-subs">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Subscriptions</div>
          <p className="text-sm text-[#4A4A4A] mt-1">Attach recurring services and send branded setup emails.</p>
        </div>
        <button
          data-testid="hq-client-subs-add"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF6A00] text-black text-[11px] font-black tracking-[0.16em] uppercase hover:bg-[#e75f00]"
        >
          <Plus size={13}/> Add subscription
        </button>
      </div>

      {items === null ? (
        <div className="mt-8 flex items-center justify-center"><Loader2 size={18} className="animate-spin text-[#8A8A8A]"/></div>
      ) : items.length === 0 ? (
        <div className="mt-6">
          <EmptyState title="No subscriptions yet" hint="Attach Orange Optimise or OrangeCare to send a setup email." testId="hq-client-subs-empty"/>
        </div>
      ) : (
        <div className="mt-5 space-y-3" data-testid="hq-client-subs-list">
          {items.map((s) => (
            <SubscriptionCard
              key={s.id}
              sub={s}
              busy={busyId === s.id}
              onResend={setPreviewFor}
              onCancel={cancelSub}
            />
          ))}
        </div>
      )}

      <AddSubscriptionModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        businessId={businessId}
        defaultContact={primaryContact}
        defaultEmail={primaryEmail}
        defaultName={businessName}
        onDraftCreated={onDraftCreated}
      />
      <SubscriptionEmailPreviewModal
        open={!!previewFor}
        onClose={() => { setPreviewFor(null); reload(); }}
        businessId={businessId}
        subscriptionId={previewFor?.subscription_id}
        defaultTo={previewFor ? { email: previewFor.to_email, name: previewFor.to_name } : null}
        onSent={() => { setPreviewFor(null); reload(); }}
      />
    </Card>
  );
}

// ============================================================
// Subscription card — bundle-aware
// ============================================================

function SubscriptionCard({ sub, busy, onResend, onCancel }) {
  const s = sub;
  const isBundle = !!s.bundle_id;
  const isCombined = isBundle && (s.product === "combined" || (s.bundle_service_keys || []).length >= 2);
  const canResend = !isBundle && ["pending_setup", "awaiting_payment"].includes(s.status);
  const canCancel = !["cancelled"].includes(s.status);
  const approveUrl = s.order?.approve_url;

  // Product label
  const productLabel = useMemo(() => {
    if (isCombined) return "OrangeCare + Orange Optimise Pro";
    if (isBundle && s.product === "orangecare") return "OrangeCare";
    if (isBundle && s.product === "optimise_pro") return "Orange Optimise Pro";
    if (s.service_key === "orange_optimise") return "Orange Optimise";
    if (s.service_key === "orange_care") return "OrangeCare";
    return s.plan?.name || s.plan_key || "Subscription";
  }, [isBundle, isCombined, s]);

  // Immutable price lines (bundle) or single-line (legacy).
  const lines = useMemo(() => {
    if (isBundle && Array.isArray(s.bundle_lines) && s.bundle_lines.length) {
      return s.bundle_lines.map((l) => ({
        label:  l.label,
        key:    l.service_key,
        amount: l.amount_minor,
      }));
    }
    return [{
      label:  s.plan?.name || SERVICE_LABELS[s.service_key] || productLabel,
      key:    s.service_key || "line",
      amount: s.amount_minor,
    }];
  }, [isBundle, s, productLabel]);

  const currency = s.currency || "GBP";
  const cadence  = s.cadence || s.billing_interval || "monthly";
  const cadenceLabel = cadence === "annual" ? "year" : "month";
  const serviceLabel = isBundle && s.service_type
    ? SERVICE_LABELS[s.service_type] || s.service_type
    : SERVICE_LABELS[s.service_key] || null;
  const entitlements = Array.isArray(s.entitlements_activated) ? s.entitlements_activated : [];
  const bundleServiceKeys = Array.isArray(s.bundle_service_keys) && s.bundle_service_keys.length
    ? s.bundle_service_keys
    : (s.service_key ? [s.service_key] : []);
  const agreements = Array.isArray(s.agreements) ? s.agreements : [];

  return (
    <div
      className={`rounded-2xl border ${isCombined ? "border-[#FCE1C7] bg-[#FFF7EE]" : "border-[#EAEAEA] bg-white"} p-5`}
      data-testid={`hq-client-sub-row-${s.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#FF6A00]">
            {isCombined ? "Combined subscription" : "Subscription"}
          </div>
          <div className="mt-1 font-black text-lg" data-testid={`hq-client-sub-product-${s.id}`}>{productLabel}</div>
          {serviceLabel && (
            <div className="mt-0.5 text-sm text-[#4A4A4A]" data-testid={`hq-client-sub-service-${s.id}`}>
              Service: <span className="font-bold">{serviceLabel}</span>
            </div>
          )}
        </div>
        <div className="text-right shrink-0 space-y-2">
          <StatusPill status={s.status}/>
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-[#8A8A8A]">
            {cadence} · {currency}
          </div>
        </div>
      </div>

      {/* Meta grid */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
        <Cell label="Started" value={fmtDate(s.started_at || s.activated_at)}/>
        <Cell label="Next payment" value={fmtDate(s.next_billing_at)}/>
        <Cell label="Provider" value={(s.provider || "—").toUpperCase()}/>
        <Cell label="Reference" value={s.order?.agreement_reference || (agreements[0]?.agreement_reference) || "—"} mono/>
      </div>

      {/* Price breakdown (immutable snapshot) */}
      <div className="mt-4 rounded-xl border border-[#EAEAEA] p-4 bg-white" data-testid={`hq-client-sub-price-${s.id}`}>
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Price (as agreed)</div>
        <div className="mt-2 space-y-1 text-sm">
          {lines.map((l) => (
            <div key={l.key} className="flex items-center justify-between" data-testid={`hq-client-sub-price-line-${s.id}-${l.key}`}>
              <span>{l.label}</span>
              <span className="font-bold tabular-nums">{money(l.amount, currency)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between pt-2 border-t border-[#EAEAEA] text-base">
            <span className="font-black">Total</span>
            <span className="font-black tabular-nums" data-testid={`hq-client-sub-price-total-${s.id}`}>
              {money(s.amount_minor, currency)} / {cadenceLabel}
            </span>
          </div>
        </div>
      </div>

      {/* Entitlements — canonical stored list */}
      {bundleServiceKeys.length > 0 && (
        <div className="mt-4" data-testid={`hq-client-sub-entitlements-${s.id}`}>
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-2">Entitlements</div>
          <div className="flex flex-wrap gap-2">
            {bundleServiceKeys.map((k) => {
              const active = entitlements.includes(k);
              return (
                <span
                  key={k}
                  data-testid={`hq-client-sub-entitlement-${s.id}-${k}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black tracking-[0.10em] uppercase border ${
                    active ? "bg-[#EBFBF2] text-[#0FA958] border-[#B7EBCF]" : "bg-[#F6F5F2] text-[#8A8A8A] border-[#EAEAEA]"
                  }`}
                >
                  {active
                    ? <CheckCircle2 size={11}/>
                    : <Circle size={11}/>}
                  {SERVICE_LABELS[k] || k}
                  <span className="ml-1 text-[9px]">{active ? "ACTIVE" : "INACTIVE"}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Agreement statuses */}
      {agreements.length > 0 && (
        <div className="mt-4" data-testid={`hq-client-sub-agreements-${s.id}`}>
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-2">Agreements</div>
          <div className="space-y-1 text-[12px]">
            {agreements.map((a) => (
              <div key={a.id} className="flex items-center justify-between border-t border-[#EAEAEA] pt-1"
                   data-testid={`hq-client-sub-agreement-${s.id}-${a.id}`}>
                <div className="min-w-0">
                  <span className="font-black">{SERVICE_LABELS[a.service_type] || (a.service_type || "").replace(/_/g, " ")}</span>
                  <span className="text-[#8A8A8A]"> · {a.agreement_reference} · v{a.template_version}</span>
                </div>
                <div className="shrink-0 flex items-center gap-2 text-[10px]">
                  <span className={`px-2 py-0.5 rounded-full font-black tracking-[0.10em] uppercase ${
                    a.status === "ACCEPTED"
                      ? "text-[#0FA958] bg-[#EBFBF2] border border-[#B7EBCF]"
                      : "text-[#8A4A00] bg-[#FFF7EE] border border-[#FCE1C7]"
                  }`}>
                    {a.status}
                  </span>
                  <span className="text-[#8A8A8A]">{fmtDate(a.accepted_at || a.issued_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex items-center justify-end gap-1">
        {busy && <Loader2 size={13} className="animate-spin text-[#8A8A8A] mr-1"/>}
        {canResend && (
          <button
            data-testid={`hq-client-sub-resend-${s.id}`}
            onClick={() => onResend({
              subscription_id: s.id,
              to_email: s.order?.customer_email,
              to_name: s.order?.customer_name,
            })}
            className="p-1.5 rounded-full text-[#FF6A00] hover:bg-[#FFF7EE]"
            title="Preview & send setup email"
          >
            <Mail size={14}/>
          </button>
        )}
        {approveUrl && (
          <button
            data-testid={`hq-client-sub-copylink-${s.id}`}
            onClick={() => { navigator.clipboard?.writeText(approveUrl); toast.success("Payment link copied"); }}
            className="p-1.5 rounded-full text-[#4A4A4A] hover:bg-[#F2F2F2]"
            title="Copy payment link"
          >
            <Copy size={14}/>
          </button>
        )}
        {canCancel && (
          <button
            data-testid={`hq-client-sub-cancel-${s.id}`}
            onClick={() => onCancel(s.id)}
            disabled={busy}
            className="p-1.5 rounded-full text-[#C1350B] hover:bg-[#FFECEC] disabled:opacity-40"
            title="Cancel subscription"
          >
            <XCircle size={14}/>
          </button>
        )}
      </div>
    </div>
  );
}

function Cell({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] tracking-[0.18em] uppercase font-bold text-[#8A8A8A]">{label}</div>
      <div className={`font-bold truncate ${mono ? "font-mono text-[11px]" : ""}`}>{value}</div>
    </div>
  );
}
